import { lowerCase } from "lodash/fp";

import {
  getFormattedDuration,
  getFormattedRemainingDuration,
} from "@app/analytics/utils";
import { formatVideoTimeFromSeconds } from "@app/analytics/utils";

import { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import { Locked, Overlay, VideoOptionButton } from "./styles";

interface VideoOptionCommon {
  icon: string;
  label: string;
  locked?: boolean;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}

interface VideoOptionDuration extends VideoOptionCommon {
  duration?: {
    /** Total video duration */
    total: number;
    /** Remaining video duration. If undefined, will display the total video duration */
    remaining?: number;
    /** Unit of duration (Defaults to seconds) */
    unit?: "minutes" | "seconds";
  };
}

interface VideoOptionTimestamp extends VideoOptionCommon {
  timestamp: number;
}

export interface VideoSelectionOverlayProps {
  options: (VideoOptionTimestamp | VideoOptionDuration)[];
}

const VideoSelectionOverlay = ({ options }: VideoSelectionOverlayProps) => {
  return (
    <Overlay variant="center" spacing={1} className="video-selection-overlay">
      {options.map((option) => {
        const {
          disabled = false,
          icon,
          label,
          locked = false,
          onClick,
          primary = false,
        } = option;

        let formattedTimeText = null;
        if ("duration" in option) {
          const { duration } = option;
          const durationOpts = {
            unit: duration?.unit ?? "seconds",
            round: true,
          };

          formattedTimeText =
            duration == null
              ? null
              : duration.remaining
                ? getFormattedRemainingDuration(
                    duration.remaining,
                    durationOpts
                  )
                : getFormattedDuration(duration.total, durationOpts);
        } else if ("timestamp" in option) {
          formattedTimeText = formatVideoTimeFromSeconds(option.timestamp);
        }

        return (
          <VideoOptionButton
            disabled={disabled}
            disableRipple={locked}
            key={icon}
            onClick={onClick}
            primary={!locked && primary}
          >
            <Row variant="apart" sx={{ width: "100%" }}>
              <Row variant="left">
                {locked && (
                  <TooltipWrapper title={`Upgrade to view ${lowerCase(label)}`}>
                    <Locked />
                  </TooltipWrapper>
                )}
                {label}
              </Row>
              <Row variant="right">
                {icon} {formattedTimeText}
              </Row>
            </Row>
          </VideoOptionButton>
        );
      })}
    </Overlay>
  );
};

export default VideoSelectionOverlay;
